import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";

import { UserProvider } from "@/contexts/UserContext";
import { PlaylistProvider } from "@/contexts/PlaylistContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

import AuthPage from "./pages/AuthPage";
import AppPage from "./pages/AppPage";
import JoinPage from "./pages/JoinPage";
import NotFound from "./pages/NotFound";
import Documentation from "./pages/Documentation";
import AppLayout from "./layouts/AppLayout";
import WatchPage from "./pages/WatchPage";
import BrowserPage from "./pages/BrowserPage";
import ChatPage from "./pages/ChatPage";
import TheaterPage from "./pages/TheaterPage";
import FriendsPage from "./pages/FriendsPage";
import ProfilePage from "./pages/ProfilePage";
import PublicProfilePage from "./pages/PublicProfilePage";
import RoomsPage from "./pages/RoomsPage";
import RoomDetailsPage from "./pages/RoomDetailsPage";
import SplashScreen from "@/components/SplashScreen";
import LegalPage from "./pages/LegalPage";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <SplashScreen isVisible={true} />;
  if (!isAuthenticated) return <Navigate to="/auth" state={{ from: location }} replace />;
  return <>{children}</>;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Navigate to="/auth" replace />} />
    <Route path="/auth" element={<AuthPage />} />
    <Route path="/documentation" element={<Documentation />} />
    <Route path="/join" element={<JoinPage />} />

    <Route path="/privacy" element={<LegalPage defaultType="privacy" />} />
    <Route path="/terms" element={<LegalPage defaultType="terms" />} />
    <Route path="/security" element={<LegalPage defaultType="security" />} />

    <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
      <Route path="/app" element={<AppPage />} />
      <Route path="/watch" element={<WatchPage />} />
      <Route path="/theater" element={<TheaterPage />} />
      <Route path="/browser" element={<BrowserPage />} />
      <Route path="/rooms" element={<RoomsPage />} />
      <Route path="/rooms/:id" element={<RoomDetailsPage />} />
      <Route path="/chat" element={<ChatPage />} />
      <Route path="/chat/:friendId" element={<ChatPage />} />
      <Route path="/friends" element={<FriendsPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/profile/:userId" element={<PublicProfilePage />} />
    </Route>

    <Route path="*" element={<NotFound />} />
  </Routes>
);

const App = () => {
  if (typeof window !== 'undefined' && (window as any).bootStep) {
    (window as any).bootStep('App Components Initializing...');
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <PlaylistProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
              <Analytics />
            </TooltipProvider>
          </PlaylistProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;

